<section class="review-shell bg-white px-4 py-4 text-text-primary sm:px-5 lg:px-6">
  <div class="review-page">
    <header class="flex flex-col gap-4 px-2 py-2 sm:px-3 lg:flex-row lg:items-center lg:justify-between">
      <div class="flex items-start gap-3">
        <button
          type="button"
          class="review-back-button"
          aria-label="Back to test series"
          (click)="goBack()">
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" class="h-4 w-4">
            <path d="M11.667 5L6.667 10L11.667 15" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>

        <div>
          <h1 class="sub-heading font-semibold">
            Exam Review
            @if (examName()) {
              <span class="text-text-secondary">- {{ examName() }}</span>
            }
          </h1>
          <p class="sub-paragraph mt-1 text-text-secondary">Review your answers, explanations, and question statuses.</p>
        </div>
      </div>

      @if (showExamSummary()) {
        <div class="flex sm:justify-end">
          <app-primary-button
            text="Exam Summary"
            ariaLabel="Go to exam summary"
            buttonClass="text-sm font-semibold rounded-lg px-6 py-2"
            (buttonClick)="goToExamSummary()" />
        </div>
      }
    </header>

    <div class="review-content-grid">
      <div class="review-main-column">

        <!-- Question Detail Section -->
        <div class="review-card flex-1 min-h-0 flex flex-col overflow-hidden bg-white">
          <div class="review-main-scroll-area p-3 sm:p-4">
            @if (isQuestionLoading()) {
              <div class="flex items-center justify-center py-16">
                <div class="loading-spinner"></div>
              </div>
            } @else if (activeQuestionDetail()) {
              <section class="pb-2">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex items-center gap-3">
                  <p class="text-sm font-bold text-text-secondary">Question {{ activeQuestionDetail()!.questionNumber }}</p>
                  <div class="flex flex-wrap items-center gap-2">
                    @for (badge of getQuestionBadges(); track badge.label) {
                      <span class="review-status-badge" [class]="badge.cssClass">
                        {{ badge.label }}
                      </span>
                    }
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  @if (activeQuestionDetail()!.subject) {
                    <span class="text-xs font-medium text-text-secondary bg-slate-100 px-2 py-1 rounded">
                      {{ activeQuestionDetail()!.subject }}
                    </span>
                  }
                  @if (activeQuestionDetail()!.topic) {
                    <span class="text-xs font-medium text-text-secondary bg-slate-100 px-2 py-1 rounded">
                      {{ activeQuestionDetail()!.topic }}
                    </span>
                  }
                  <button
                    type="button"
                    class="review-bookmark-button"
                    [class.review-bookmark-active]="isBookmarked()"
                    [disabled]="!activeQuestionDetail() || isQuestionBookmarkLoading(activeQuestionDetail()!.questionId)"
                    (click)="bookmarkActiveQuestion()">
                    @if (isBookmarked()) {
                      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="h-5 w-5 text-white">
                        <path d="M6.667 4.167H13.333C13.7932 4.167 14.1663 4.54 14.1663 5V15L10.4163 12.5C10.1643 12.332 9.83535 12.332 9.58333 12.5L5.83333 15V5C5.83333 4.54 6.20643 4.167 6.667 4.167Z" />
                      </svg>
                    } @else {
                      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" class="h-5 w-5">
                        <path d="M6.667 4.167H13.333C13.7932 4.167 14.1663 4.54 14.1663 5V15L10.4163 12.5C10.1643 12.332 9.83535 12.332 9.58333 12.5L5.83333 15V5C5.83333 4.54 6.20643 4.167 6.667 4.167Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                      </svg>
                    }
                    <span>
                      {{ isBookmarked() ? 'Bookmarked' : (isQuestionBookmarkLoading(activeQuestionDetail()!.questionId) ? 'Saving...' : 'Bookmark') }}
                    </span>
                  </button>
                </div>
              </div>

              <div class="mt-2">
                <h2 class="review-question-title question-prompt-html" [innerHTML]="questionHtml()"></h2>

                @if (activeQuestionDetail()!.questionImageUrls.length) {
                  <div class="mt-4 space-y-3">
                    @for (url of activeQuestionDetail()!.questionImageUrls; track url; let i = $index) {
                      <div class="rounded-lg border border-slate-200 overflow-hidden bg-slate-50 p-2 flex justify-center">
                        <img
                          [src]="url"
                          [alt]="'Question image ' + (i + 1)"
                          class="question-image !m-0"
                          loading="lazy" />
                      </div>
                    }
                  </div>
                }

                <div class="mt-5 space-y-4">
                  @for (option of activeQuestionDetail()!.options; track option.key; let i = $index) {
                    <article [class]="getOptionClass(option)">
                      <div class="flex justify-between w-full items-center gap-3">
                        <span [class]="getOptionLabelClass(option)">{{ getOptionLetter(i) }}</span>
                        <div class="min-w-0 flex-1">
                          <div class="review-option-text question-prompt-html" [innerHTML]="option.text | richHtml"></div>
                        </div>
                        @if (option.isSelected && !option.isCorrect) {
                          <div class="review-answer-tag review-answer-tag-incorrect">
                            <span>Your Answer</span>
                            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" class="h-4 w-4">
                              <path d="M13.333 6.667L6.66634 13.333" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
                              <path d="M6.66634 6.667L13.333 13.333" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
                            </svg>
                          </div>
                        }

                        @if (option.isCorrect) {
                          <div class="review-answer-tag review-answer-tag-correct">
                            <span>Correct Answer</span>
                            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" class="h-4 w-4">
                              <path d="M5.83301 10.417L8.33301 12.917L14.1663 7.08366" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                          </div>
                        }
                      </div>
                    </article>
                  }
                </div>
              </div>
            </section>

            @if (activeQuestionDetail()!.journey; as journey) {
              <app-answer-journey class="mt-6" [journey]="journey"></app-answer-journey>
            }

            @if (explanationHtml() || activeQuestionDetail()!.explanationImageUrls.length || activeQuestionDetail()!.reference) {
              <section class="review-explanation-card mt-6">
                <div class="flex items-center gap-2 text-brand-primary">
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" class="h-5 w-5">
                    <path d="M6.667 3.333H13.333C14.2535 3.333 15 4.0795 15 5V15C15 15.9205 14.2535 16.667 13.333 16.667H6.667C5.7465 16.667 5 15.9205 5 15V5C5 4.0795 5.7465 3.333 6.667 3.333Z" stroke="currentColor" stroke-width="1.5" />
                    <path d="M8.333 7.5H11.667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                    <path d="M8.333 10H11.667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                  </svg>
                  <h3 class="text-base font-semibold">Explanation</h3>
                </div>

                @if (explanationHtml()) {
                  <div class="mt-3 text-sm leading-7 text-slate-600 question-prompt-html" [innerHTML]="explanationHtml()"></div>
                }

                @if (activeQuestionDetail()!.explanationImageUrls.length) {
                  <div class="mt-4 space-y-3">
                    @for (url of activeQuestionDetail()!.explanationImageUrls; track url; let i = $index) {
                      <div class="rounded-lg border border-slate-200 overflow-hidden bg-slate-50 p-2 flex justify-center">
                        <img
                          [src]="url"
                          [alt]="'Explanation image ' + (i + 1)"
                          class="question-image !m-0"
                          loading="lazy" />
                      </div>
                    }
                  </div>
                }

                @if (activeQuestionDetail()!.reference) {
                  <div class="mt-4 border-t border-slate-200 pt-3 text-sm text-slate-600">
                    <span class="font-semibold text-text-primary">Reference:</span>
                    @if (referenceUrl(activeQuestionDetail()!.reference); as url) {
                      <a [href]="url" target="_blank" rel="noopener noreferrer"
                        class="text-primary underline break-all hover:opacity-80">{{ activeQuestionDetail()!.reference }}</a>
                    } @else {
                      {{ activeQuestionDetail()!.reference }}
                    }
                  </div>
                }
              </section>
            }
          } @else {
            <div class="flex items-center justify-center py-16 text-text-secondary text-sm">
              Select a question to view its details
            </div>
          }
          </div>
        </div>

        <div class="review-footer-bar">
          <div class="sm:justify-self-start">
            <app-outline-button
              text="Previous Question"
              ariaLabel="Previous review question"
              buttonClass="text-sm font-semibold rounded-lg px-6 py-3"
              [disabled]="!canGoPrevious()"
              (buttonClick)="showPreviousQuestion()" />
          </div>

          <p class="text-center text-[15px] font-semibold text-[#707070]">
            @if (activeQuestionDetail()) {
              Question {{ activeQuestionDetail()!.questionNumber }} of {{ totalQuestionCount() }}
            }
          </p>

          <div class="sm:justify-self-end">
            <app-primary-button
              text="Next Question"
              ariaLabel="Next review question"
              buttonClass="text-sm font-semibold rounded-lg px-6 py-3"
              [disabled]="!canGoNext()"
              (buttonClick)="showNextQuestion()" />
          </div>
        </div>
      </div>

      <aside class="review-sidebar-column">
        <section class="review-card review-sidebar-card-shell p-4 sm:p-5 flex flex-col h-full bg-white">
          <div class="flex flex-col gap-4 bg-mock-indigo-50 p-3 rounded-xl border border-indigo-100 mb-4 shrink-0">
            <div class="flex flex-wrap gap-2">
              @for (part of paletteParts(); track part.id; let index = $index) {
                <button
                  type="button"
                  class="review-part-chip"
                  [class.review-part-chip-active]="isPartActive(index)"
                  (click)="selectPartByIndex(index)">
                  {{ part.label }}
                </button>
              }
            </div>

            <div class="flex flex-wrap items-center gap-x-3 gap-y-2 pt-3 border-t border-indigo-200/60">
              @for (pill of statusPills(); track pill.id) {
                <div class="flex items-center gap-1.5">
                  <span class="review-status-dot" [class]="pill.toneClass"></span>
                  <span class="text-[11px] font-medium text-slate-600">{{ pill.label }}</span>
                </div>
              }
            </div>
          </div>

          <div class="flex-1 overflow-y-auto pr-1 review-main-scroll-area min-h-0">
            @if (isPaletteLoading()) {
              <div class="flex items-center justify-center py-6">
                <div class="loading-spinner"></div>
              </div>
            } @else {
              <div class="grid grid-cols-7 gap-y-3 justify-items-center pb-4 pl-1">
                @for (question of activePaletteQuestions(); track question.questionId) {
                  <button
                    type="button"
                    [class]="getPaletteButtonClass(question)"
                    [attr.aria-label]="'Open review question ' + question.questionNumber"
                    (click)="onPaletteQuestionClick(question)">
                    {{ question.questionNumber }}
                  </button>
                }
              </div>
            }
          </div>
        </section>
      </aside>
    </div>
  </div>
</section>
